import {AfterViewInit, ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {Route} from '../../enum/route';
import {User} from '../../model/user';
import {UserService} from '../../services/user/user.service';
import {combineLatest, map, Observable, take} from 'rxjs';
import {UserFormBase} from '../../components/create-user/user-form-base/user-form-base.component';
import {Store} from '@ngrx/store';
import {UserLoadActions} from '../../store/user/actions/userActions';
import {AuthService} from '../../services/auth/auth.service';
import {selectUserRoles} from '../../store/auth/selectors/auth.selectors';
import {selectUser} from '../../store/user/selectors/user.selectors';
import {selectMyCompany} from '../../store/company/selectors/company.selectors';
import {Roles} from '../../enum/roles';
import {UserPage} from '../../model/user-page';
import {MatDialog} from '@angular/material/dialog';
import {UserAppState} from '../../store/user/reducers/user.reducer';

@Component({
    selector: 'app-onboarding-user',
    templateUrl: './onboarding-user.component.html',
    styleUrls: ['../../../styles/create-something.scss', 'onboarding-user.component.scss', '../../../styles/standard-elements.scss'],
    standalone: false
})
export class OnboardingUserComponent implements OnInit, AfterViewInit {
  idNames = [];
  scrolled = 0;
  maxScrolled = 0;
  scrollPosition = [0];
  user = new User();
  titles = ['personalInformation',
    'contactInformation',
    'privacyPolicy.title'
  ];
  company$ = this.store.select(selectMyCompany);

  allFormsValid$: Observable<boolean>;
  validChildForm: Observable<boolean>[] = [];

  @ViewChildren(UserFormBase) formComponents: QueryList<UserFormBase>;

  constructor(private readonly translate: TranslateService,
              private readonly dialog: MatDialog,
              private readonly router: Router,
              readonly userService: UserService,
              private readonly changeDetector: ChangeDetectorRef,
              public store: Store,
              public authService: AuthService,
              private readonly userStore: Store<UserAppState>,
  ) {
  }

  ngOnInit() {
    for (let i = 0; i < this.titles.length; i++) {
      this.idNames.push('question-block' + i);
    }
  }

  scroll(id) {
    document.getElementById(id).scrollIntoView();
  }

  onWindowScroll(el: HTMLElement) {
    // margin must be equal to the one given in style class form-outer-container
    const margin = 64;
    this.scrollPosition = [0];
    let scrollTop = el.scrollTop + window.innerHeight / 3;
    for (let childrenKey in el.children) {
      this.scrollPosition.push(el.children[childrenKey].scrollHeight + margin + this.scrollPosition[this.scrollPosition.length - 1]);
    }
    this.scrolled = 0;
    for (let i = 1; i < this.scrollPosition.length; i++) {
      if (scrollTop > this.scrollPosition[i] && this.titles[i]) {
        this.scrolled = i;
      }
    }
    for (let i = 0; i < this.scrolled; i++) {
      this.formComponents.get(i).formGroup.markAllAsTouched();
      this.formComponents.get(i).formGroup.updateValueAndValidity();
    }
    this.maxScrolled = Math.max(this.maxScrolled, this.scrolled);
    this.scroll(this.idNames[this.scrolled] + 'button');
  }


  saveUser() {
    this.user = new User();
    for (let form of this.formComponents) {
      form.updateUser(this.user);
    }
    this.user.id = this.authService.getUserIdFromAccessToken();
    this.userService.saveUser(this.user).subscribe(() => {
      this.store.dispatch(UserLoadActions.loaded({user: new UserPage(this.user), loading: false}));
      this.store.select(selectUserRoles).subscribe(roles => {
        if (roles.includes(Roles.ROLE_CORPORATE_ADMIN) || roles.includes(Roles.ROLE_DIRECT_CORPORATE_ADMIN)
          || roles.includes(Roles.ROLE_REPRESENTATIVE)) {
          this.userStore.dispatch(UserLoadActions.load({userId: this.user.id}));
          this.rerouteToMyCompany();
        } else {
          this.userStore.dispatch(UserLoadActions.load({userId: this.user.id}));
          this.rerouteToMyProfile();
        }
      });
    });

  }

  rerouteToMyCompany() {
    this.store.select(selectMyCompany).pipe(take(1)).subscribe(
      (company) => {
        let navigateUrl: string = `/${Route.COMPANY_PROFILE}/${company.companyId}`;
        this.router.navigateByUrl(navigateUrl).then();
      }
    );
  }

  rerouteToMyProfile() {
    this.store.select(selectUser).pipe(take(1)).subscribe(
      (user) => {
        let navigateUrl: string = `/${Route.USER_PAGE}/${user.id}`;
        this.router.navigateByUrl(navigateUrl).then();
      }
    );
  }

  ngAfterViewInit(): void {
    const obs: Observable<boolean>[] = [];
    for (let formbase of this.formComponents) {
      obs.push(formbase.validStatus());
      this.validChildForm.push(formbase.validStatus());
    }
    this.allFormsValid$ = combineLatest(obs)
      .pipe(
        map((array: boolean[]) => array.every(value => value)),
      );
    this.changeDetector.detectChanges();
  }

  logout() {
    this.authService.logout();
  }
}
